import { useContext, useCallback } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { clearAllCaches, clearAuthCache, supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export const useAuth = () => {
  const context = useContext(AuthContext);
  const navigate = useNavigate();
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  // Add a function to delete user account
  const deleteAccount = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        return { success: false, message: 'No active session' };
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete account');
      }

      clearAllCaches();
      await context.signOut();
      navigate('/', { replace: true });

      return { success: true, message: 'Account deleted successfully' };
    } catch (error) {
      console.error('Error deleting account:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error deleting account'
      };
    }
  }, [context, navigate]);
  
  // Add a wrapper around signOut to clear caches when signing out
  const enhancedSignOut = useCallback(async () => {
    try {
      // Get the current user ID before signing out
      const { data: { user } } = await supabase.auth.getUser();
      
      // Clear auth session from local storage
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('supabase.auth.expires_at');
      localStorage.removeItem('supabase.auth.refresh_token');
      localStorage.removeItem('subscriptionCache');
      localStorage.removeItem('current_subscription_id');
      localStorage.removeItem('selectedPlan');
      
      // Clear all caches before signing out
      clearAllCaches();
      
      const result = await context.signOut();

      return result;
    } catch (error) {
      console.error('Error in signOut:', error);
      throw error;
    }
  }, [context]);
  
  // Add a function to handle refresh token errors
  const handleRefreshTokenError = useCallback(async () => {

    // Define public routes that don't require authentication
    const publicRoutes = ['/login', '/signup', '/home', '/', '/features', '/about', '/terms', '/privacy', '/pricing'];
    
    // Check if current route is public
    const isPublicRoute = publicRoutes.some(route => 
      window.location.pathname === route || 
      window.location.pathname.startsWith(route + '/')
    );
    
    // If we're already on a public route, just clear the cache but don't redirect
    if (isPublicRoute) {
      clearAuthCache();
      return;
    }
    
    try {
      // Get the current user ID before signing out
      const { data: { user } } = await supabase.auth.getUser();
      
      // Clear all caches and local storage
      clearAllCaches();
      
      await supabase.auth.signOut();


      window.location.href = '/login';
    } catch (error) {
      console.error('Error handling refresh token error:', error);
      // Force redirect to login even if there's an error
      window.location.href = '/login';
    }
  }, []);
  
  return {
    ...context,
    signOut: enhancedSignOut,
    handleRefreshTokenError,
    deleteAccount
  };
};